"use client";

import { useEffect, useRef } from "react";

type NeonIsometricMazeProps = {
  className?: string;
};

export function NeonIsometricMaze({ className }: NeonIsometricMazeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    let time = 0;
    let width = 0;
    let height = 0;
    let animationFrameId = 0;
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );

    const drawMaze = () => {
      if (width === 0 || height === 0) {
        return;
      }

      const size = Math.min(width, height) / 9;
      const gridWidth = Math.ceil(width / size) * 2;
      const gridHeight = Math.ceil(height / (size * 0.5)) * 2;
      const centerX = width / 2;
      const centerY = height / 2;

      for (let y = -gridHeight; y < gridHeight; y += 1) {
        for (let x = -gridWidth; x < gridWidth; x += 1) {
          const pointX = centerX + ((x - y) * size) / 2;
          const pointY = centerY + ((x + y) * size) / 4;
          const distance = Math.sqrt(x * x + y * y);
          const maxDistance = Math.sqrt(
            gridWidth * gridWidth + gridHeight * gridHeight,
          );
          const depth = 1 - distance / maxDistance;
          const extrusion =
            size * depth * Math.abs(Math.sin(distance * 0.5 + time));

          context.beginPath();
          context.moveTo(pointX, pointY - extrusion);
          context.lineTo(pointX + size / 2, pointY - size / 2 - extrusion);
          context.lineTo(pointX + size, pointY - extrusion);
          context.lineTo(pointX + size, pointY);
          context.lineTo(pointX + size / 2, pointY + size / 2);
          context.lineTo(pointX, pointY);
          context.closePath();

          const gradient = context.createLinearGradient(
            pointX,
            pointY - extrusion,
            pointX + size,
            pointY,
          );
          gradient.addColorStop(0, "rgba(0,255,255,.8)");
          gradient.addColorStop(1, "rgba(255,0,255,.8)");
          context.fillStyle = gradient;
          context.fill();
          context.strokeStyle = "rgba(255,255,0,.5)";
          context.stroke();

          context.beginPath();
          context.moveTo(pointX, pointY);
          context.lineTo(pointX, pointY - extrusion);
          context.moveTo(pointX + size, pointY);
          context.lineTo(pointX + size, pointY - extrusion);
          context.moveTo(pointX + size / 2, pointY + size / 2);
          context.lineTo(pointX + size / 2, pointY - size / 2 - extrusion);
          context.strokeStyle = "rgba(255,255,255,.3)";
          context.stroke();
        }
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;

      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, Math.floor(width * pixelRatio));
      canvas.height = Math.max(1, Math.floor(height * pixelRatio));
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      context.fillStyle = "#000";
      context.fillRect(0, 0, width, height);
      drawMaze();
    };

    const animate = () => {
      context.fillStyle = "rgba(0,0,0,.1)";
      context.fillRect(0, 0, width, height);
      drawMaze();
      time += 0.01;
      animationFrameId = requestAnimationFrame(animate);
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    resize();

    if (reducedMotionQuery.matches) {
      drawMaze();
    } else {
      animate();
    }

    return () => {
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden className={className} />;
}
